import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Breadcrumb from "../components/Breadcrumb";
import LoadingComponent from "../components/ui/LoadingComponent";
import { handleCustomResponse } from "../utils/response";
import { staffApi } from "../Apis/Api";
import ErrorAlert from "../components/ui/ErrorAlert";
import InfoModal from "../components/ui/InfoeModal";

const RedeemRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasMore: false,
  });
  const [processingId, setProcessingId] = useState(null); // 👈 Changed: Track which row is processing
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRequestId, setPendingRequestId] = useState(null);
  const [error, setError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    fetchRedeemRequests(1, true);
  }, []);

  const fetchRedeemRequests = async (page = 1, resetData = false) => {
    setError(null)
    try {
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const params = {
        status: "PENDING",
        per_page: 10,
        page: page,
      };

      const response = await staffApi.getStaffRedeemRequests(params);

      if (response.success) {
        const newRequests = response.data || [];

        const formattedRequests = newRequests.map((req) => {
          let type, amount, currency, points, description;

          if (parseFloat(req.points_requested) > 0) {
            type = "points";
            points = parseFloat(req.points_requested);
            amount = 0;
            currency = "";
            description = `Points redeem request (${points} pts)`;
          } else {
            type = "currency";
            amount = parseFloat(req.currency_equivalent);
            currency = req.currency_code || "USD";
            points = 0;
            description = `Currency redeem request (${currency} ${amount})`;
          }

          return {
            id: req.redeem_id,
            staffId: req.staff_id,
            staffName: req.staff_name || `Staff ${req.staff_id}`,
            type: type,
            amount: amount,
            currency: currency,
            points: points,
            description: description,
            date: req.created_datetime,
            status: req.status?.toLowerCase() || "pending",
          };
        });

        if (resetData || page === 1) {
          setRequests(formattedRequests);
        } else {
          setRequests((prev) => [...prev, ...formattedRequests]);
        }

        setPagination({
          currentPage: response.pagination?.current_page || page,
          totalPages: response.pagination?.last_page || 1,
          hasMore:
            (response.pagination?.current_page || page) <
            (response.pagination?.last_page || 1),
        });
      } else {
        // handleCustomResponse(
        //   "error",
        //   response.message || "Failed to load redeem requests"
        // );
      }
    } catch (error) {
      console.error("Error fetching redeem requests:", error);
      // handleCustomResponse("error", "Failed to load redeem requests");
      setError(true)
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreRequests = () => {
    if (!isLoadingMore && pagination.hasMore) {
      fetchRedeemRequests(pagination.currentPage + 1, false);
    }
  };

  const handleProcessRequest = async (id, action) => {
    if (!id || id === undefined) {
      handleCustomResponse("error", "Invalid redeem request ID");
      return;
    }
    setPendingRequestId(id);
    setPendingAction(action);
    setShowConfirmModal(true);
  };

  const confirmProcessRequest = async () => {
    if (processingId || !pendingRequestId || !pendingAction) return; // 👈 Changed

    try {
      setProcessingId(pendingRequestId); // 👈 Changed: Set specific ID being processed
      const response = await staffApi.processRedeemRequest(
        pendingRequestId,
        pendingAction
      );

      if (response.success) {
        handleCustomResponse(
          "success",
          `Redeem request ${pendingAction.toLowerCase()}ed successfully!`
        );
        fetchRedeemRequests(1, true); // Refresh the list
      } else {
        handleCustomResponse(
          "error",
          response.message ||
            `Failed to ${pendingAction.toLowerCase()} redeem request`
        );
      }
    } catch (error) {
      console.error(
        `Error ${pendingAction.toLowerCase()}ing redeem request:`,
        error
      );
      handleCustomResponse(
        "error",
        `Failed to ${pendingAction.toLowerCase()} redeem request`
      );
    } finally {
      setProcessingId(null); // 👈 Changed
      setShowConfirmModal(false);
      setPendingRequestId(null);
      setPendingAction(null);
    }
  };

  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(Math.abs(amount));
  };

  const formatPoints = (points) => {
    return `${points.toLocaleString()} pts`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRequestAmount = (req) => {
    if (req.type === "points" && req.points > 0) {
      return formatPoints(req.points);
    } else if (req.type === "currency" && req.amount > 0) {
      return formatCurrency(req.amount, req.currency);
    } else {
      return "-";
    }
  };

  // Show full screen loader on initial load
  if (isLoading && requests.length === 0) {
    return (
      <div>
        <Breadcrumb title="Redeem Requests" />
        <LoadingComponent />
      </div>
    );
  }
  if(error){
    return <ErrorAlert/>
  }

  return (
    <>
      <Breadcrumb title="Redeem Requests" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <div className="d-flex align-items-center justify-content-between mb-24">
            <div className="d-flex align-items-center gap-2">
              <Icon
                icon="solar:history-bold"
                className="text-primary-600"
                style={{ fontSize: "20px" }}
              />
              <h5 className="mb-0">Pending Redeem Requests</h5>
            </div>
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Redeem Requests Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
          </div>

          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th scope="col" className="text-nowrap">
                    Staff
                  </th>
                  <th scope="col" className="text-nowrap">
                    Type
                  </th>
                  <th scope="col" className="d-none d-md-table-cell">
                    Description
                  </th>
                  <th scope="col" className="text-nowrap">
                    Amount
                  </th>
                  <th scope="col" className="d-none d-sm-table-cell">
                    Date
                  </th>
                  <th scope="col" className="text-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.length > 0 ? (
                  requests.map((req) => (
                    <tr key={req.id}>
                      <td className="align-middle">
                        <span className="fw-medium">{req.staffName}</span>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center gap-1 gap-md-2 flex-nowrap">
                          <Icon
                            icon={
                              req.type === "points"
                                ? "heroicons:star"
                                : "heroicons:banknotes"
                            }
                            className="flex-shrink-0 text-primary-600"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="fw-medium text-capitalize d-none d-lg-inline">
                            {req.type.charAt(0).toUpperCase() +
                              req.type.slice(1)}
                          </span>
                        </div>
                        <div className="d-md-none mt-1">
                          <small className="text-secondary-light">
                            {req.description}
                          </small>
                        </div>
                      </td>
                      <td className="align-middle d-none d-md-table-cell">
                        <span className="text-secondary-light">
                          {req.description}
                        </span>
                      </td>
                      <td className="align-middle">
                        <span className="fw-semibold text-primary-600  text-nowrap">
                          {getRequestAmount(req)}
                        </span>
                        <div className="d-sm-none mt-1">
                          <small className="text-secondary-light">
                            {formatDate(req.date)}
                          </small>
                        </div>
                      </td>
                      <td className="align-middle d-none d-sm-table-cell">
                        <span className="text-secondary-light text-nowrap">
                          {formatDate(req.date)}
                        </span>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success-600 px-3 py-1 text-sm"
                            onClick={() =>
                              handleProcessRequest(req.id, "APPROVE")
                            }
                            disabled={processingId === req.id} // 👈 Changed: Only disable this row's buttons
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-danger-600 px-3 py-1 text-sm"
                            onClick={() =>
                              handleProcessRequest(req.id, "REJECT")
                            }
                            disabled={processingId === req.id} // 👈 Changed: Only disable this row's buttons
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <Icon
                        icon="solar:wallet-bold-duotone"
                        className="text-6xl text-neutral-400 mb-3"
                      />
                      <p className="text-secondary-light mb-0">
                        No pending requests found
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.hasMore && (
            <div className="text-center mt-4">
              <button
                className="btn btn-primary-600 px-4 py-2"
                onClick={loadMoreRequests}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </span>
                    Loading More...
                  </>
                ) : (
                  <>
                    <Icon icon="heroicons:arrow-down" className="me-2" />
                    Load More Requests
                  </>
                )}
              </button>
            </div>
          )}

          {requests.length > 0 && (
            <div className="text-center mt-3">
              <small className="text-secondary-light">
                {pagination.hasMore && ` • ${requests.length} requests loaded`}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Action</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-secondary-light">
                  Are you sure you want to {pendingAction?.toLowerCase()} this
                  redeem request?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingRequestId(null);
                    setPendingAction(null);
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-primary-600"
                  onClick={confirmProcessRequest}
                  disabled={processingId !== null} // 👈 Changed
                >
                  {processingId ? ( // 👈 Changed
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Processing...</span>
                      </span>
                      Processing...
                    </>
                  ) : (
                    "Yes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
    <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Redeem Request Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page is the <strong>central control panel</strong> for all <strong>Staff Redeem Requests</strong>. 
    Staff submit requests here when they want to claim their earned incentives (points or cash).
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What is a Redeem Request?</h6>
  <p className="mb-4">
    When staff complete jobs under an assigned Incentive Plan, they earn rewards. 
    Once they reach the minimum threshold, they create a Redeem Request to claim their reward.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What You See in the List</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Staff Name & ID</strong></li>
    <li className="list-group-item"><strong>Earned Amount</strong> – Points or Currency value</li>
    <li className="list-group-item"><strong>Request Date</strong></li>
    <li className="list-group-item"><strong>Current Status</strong> – Pending | Approved | Rejected</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Three Action Buttons (Per Request)</h6>
  <ul className="list-group list-group-flush mb-4">
   
    <li className="list-group-item">
      <strong>Approve (Green Check)</strong><br/>
      Confirms the reward is valid → Status becomes <strong>Approved</strong><br/>
      Staff gets notified and reward is ready for payout
    </li>
    <li className="list-group-item">
      <strong>Reject (Red Cross)</strong><br/>
      Rejects the request → You can add a reason (optional)<br/>
      Status becomes <strong>Rejected</strong> → Staff gets notified
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Note:</strong><br/>
    • Only <strong>Pending</strong> requests can be Approved or Rejected<br/>
    • Approved requests can later be processed for actual payout (cash/points transfer)<br/>
    • Rejected requests can be resubmitted by staff after correction<br/>
    • All actions are logged for audit
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Always check "View Details" before approving → Ensures 100% fair & accurate rewards!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Fast & fair redeem processing = Highly motivated staff → Better performance → Stronger team loyalty.
  </p>
</InfoModal>
    </>
  );
};

export default RedeemRequestsPage;